// Users: who exists, what they hold, and the two writes an operator needs
// (create an account, adjust a balance). Balance adjustments carry a
// reason and an idempotency key because they are money -- a double-submit
// must not charge twice, and a ledger row with no reason is unreviewable.
import { useEffect, useState } from "react";
import { adminAdjustBalance, adminCreateUser, adminListUsers, type AdminUser } from "../../api/client";
import { formatMinor } from "../../lib/format";

export default function AdminUsers() {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [error, setError] = useState<string | null>(null);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState<"USER" | "ADMIN">("USER");

  const [adjustFor, setAdjustFor] = useState<AdminUser | null>(null);
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  function load() {
    adminListUsers().then(setUsers).catch((e) => setError(e instanceof Error ? e.message : "failed to load users"));
  }
  useEffect(() => {
    load();
    // Balances move with every Task; re-read them like the rest of the cockpit.
    const timer = setInterval(load, 15000);
    return () => clearInterval(timer);
  }, []);

  async function onCreate() {
    setError(null);
    setBusy(true);
    try {
      await adminCreateUser(email, password, role);
      setEmail("");
      setPassword("");
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "failed to create user");
    } finally {
      setBusy(false);
    }
  }

  async function onAdjust() {
    if (!adjustFor) return;
    const amountMinor = Math.round(Number(amount) * 100);
    if (!Number.isFinite(amountMinor) || amountMinor === 0) {
      setError("enter a nonzero dollar amount");
      return;
    }
    setError(null);
    setBusy(true);
    try {
      await adminAdjustBalance(
        adjustFor.id,
        amountMinor,
        reason || "admin adjustment",
        "admin-ui",
        `${adjustFor.id}-${Date.now()}`,
      );
      setAdjustFor(null);
      setAmount("");
      setReason("");
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "failed to adjust balance");
    } finally {
      setBusy(false);
    }
  }

  const total = users.reduce((n, u) => n + u.balance_minor, 0);

  return (
    <>
      <div className="a-head">
        <div>
          <h1 className="a-h1">Users</h1>
          <p className="a-lede">
            Accounts are created here (ADR-0015) — there is no self-signup. The balance is money
            the user can spend, not money they have paid.
          </p>
        </div>
        <span className="a-panel__meta">
          {users.length} accounts · {formatMinor(total, "USD_CENTS")} outstanding
        </span>
      </div>

      {error && <p className="a-error">{error}</p>}

      <div className="a-tiles">
        <div className="a-tile">
          <span className="a-tile__label">Accounts</span>
          <span className="a-tile__value">{users.length}</span>
          <span className="a-tile__sub">{users.filter((u) => u.disabled_at).length} disabled</span>
        </div>
        <div className="a-tile">
          <span className="a-tile__label">Admins</span>
          <span className="a-tile__value">{users.filter((u) => u.role === "ADMIN").length}</span>
          <span className="a-tile__sub">can reach this cockpit</span>
        </div>
        <div className="a-tile">
          <span className="a-tile__label">Outstanding balance</span>
          <span className="a-tile__value a-tile__value--cyan">{formatMinor(total, "USD_CENTS")}</span>
          <span className="a-tile__sub">sum of all accounts</span>
        </div>
      </div>

      <section className="a-panel">
        <div className="a-panel__head">
          <h2 className="a-panel__title">Accounts</h2>
          <span className="a-panel__meta">click Adjust to move a balance</span>
        </div>
        <div className="a-scroll">
          <table className="a-table">
            <thead>
              <tr>
                <th>Email</th>
                <th>Role</th>
                <th className="a-table__num">Balance</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id}>
                  <td className="a-table__hi">{u.email}</td>
                  <td className="a-table__dim">{u.role}</td>
                  <td className="a-table__num">{formatMinor(u.balance_minor, "USD_CENTS")}</td>
                  <td>
                    <span className={`a-state a-state--${u.disabled_at ? "cancelled" : "active"}`}>
                      {u.disabled_at ? "disabled" : "active"}
                    </span>
                  </td>
                  <td>
                    <button className="a-btn" onClick={() => setAdjustFor(u)}>
                      Adjust
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <div className="a-grid a-grid--2">
        <section className="a-panel">
          <div className="a-panel__head">
            <h2 className="a-panel__title">Create account</h2>
            <span className="a-panel__meta">ADR-0015</span>
          </div>
          <div className="a-form">
            <label className="a-field">
              Email
              <input className="a-input" value={email} onChange={(e) => setEmail(e.target.value)} />
            </label>
            <label className="a-field">
              Password
              <input className="a-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} />
            </label>
            <label className="a-field">
              Role
              <select className="a-select" value={role} onChange={(e) => setRole(e.target.value as "USER" | "ADMIN")}>
                <option value="USER">USER</option>
                <option value="ADMIN">ADMIN</option>
              </select>
            </label>
            <button className="a-btn a-btn--primary" disabled={busy || !email || !password} onClick={() => void onCreate()}>
              Create
            </button>
          </div>
        </section>

        <section className="a-panel">
          <div className="a-panel__head">
            <h2 className="a-panel__title">Adjust balance</h2>
            <span className="a-panel__meta">{adjustFor ? adjustFor.email : "pick an account"}</span>
          </div>
          {adjustFor === null ? (
            <p className="a-empty">Choose Adjust on a row to credit or debit that account.</p>
          ) : (
            <div className="a-form">
              <label className="a-field">
                Amount (USD) — negative debits
                <input
                  className="a-input"
                  placeholder="10.00 or -5.00"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                />
              </label>
              <label className="a-field">
                Reason (recorded in the ledger)
                <input className="a-input" value={reason} onChange={(e) => setReason(e.target.value)} />
              </label>
              <p className="a-note">
                Current balance {formatMinor(adjustFor.balance_minor, "USD_CENTS")}. The adjustment is
                written to the account ledger with this reason and an idempotency key, so a
                double-submit cannot land twice.
              </p>
              <div>
                <button className="a-btn a-btn--primary" disabled={busy} onClick={() => void onAdjust()}>
                  Apply adjustment
                </button>{" "}
                <button className="a-btn" onClick={() => setAdjustFor(null)}>
                  Cancel
                </button>
              </div>
            </div>
          )}
        </section>
      </div>
    </>
  );
}
